import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Subject } from 'rxjs';
import { palette } from '../../theme/palette';
import { SEARCH_LONG_PLACEHOLDER, SEARCH_SHORT_PLACEHOLDER } from '../../constants/search';
import { SearchAutoSuggest, StyleSearchAutoSuggestIcon } from './EquipmentSearchAutoSuggestStyle';
import { useEffect, useMemo, useState } from 'react';

export interface SearchAutoSuggestProps {
  id: string;
  showCloseButton?: boolean;
  toggleWidth?: boolean;
  isMobileNavSearchElement?: boolean;
}

const closeMobileSearchBlock = new CustomEvent('closemobilesearchblock');

function EquipmentSearchAutoSuggest({
  id,
  showCloseButton,
  toggleWidth,
  isMobileNavSearchElement,
}: SearchAutoSuggestProps) {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [input, setInput] = useState<string>('');
  const [toggleWidthPlaceholder, setToggleWidthPlaceholder] =
    useState<string>(SEARCH_SHORT_PLACEHOLDER);

  const inputFocusSubject = useMemo(() => new Subject<boolean>(), []);
  const closeAutocompleteSubject = useMemo(() => new Subject<boolean>(), []);

  const initSearchElement = () => {
    const element = getPlaceholderElement();

    if (!element) return;

    document.addEventListener('click', (event) => {
      globalClickHandler(event.target, element);
    });
  };

  const globalClickHandler = (targetElement: EventTarget | null, element: HTMLElement | null) => {
    let targetEl: EventTarget | null = targetElement;
    let isClickedInside = false;

    if (!targetEl) return;

    // Check if the click is inside the search element
    do {
      if (targetEl == element) {
        isClickedInside = true;
        break;
      }
      targetEl = (targetEl as HTMLElement).parentNode;
    } while (targetEl);

    // If clicked outside the search element, set isActive to false
    if (!isClickedInside) {
      setIsActive(false);
    }
  };

  const getPlaceholderElement = () => {
    return document.getElementById(id);
  };

  const keyDownHandler = () => {
    setTimeout(() => {
      if (!document.activeElement) return;

      const element = getPlaceholderElement();

      // Check if search element or any of its descendant is not in focus
      if (element !== document.activeElement && !element?.contains(document.activeElement)) {
        setIsActive(false);
      }
    }, 0);
  };

  const removeWebflowSearchElements = () => {
    const removeElement = (elementId: string) => {
      const element = document.getElementById(elementId);
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    };

    // Remove desktop search element
    removeElement('es-search-element');
    // Remove mobile search element
    removeElement('es-mobile-search-element');
  };

  const togglePlaceholderWidth = (toggle: boolean) => {
    const element = getPlaceholderElement();
    if (element) {
      if (toggle) {
        element.style.width = '100%';
      } else {
        element.style.width = 'calc(50% - 5px)';
      }
    }
  };

  const onSearchIconClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (toggleWidth) {
      event.preventDefault();
      event.stopPropagation();
      setIsActive((prev) => !prev);
    }
  };

  const openHandler = () => {
    setIsActive(true);
    setToggleWidthPlaceholder(SEARCH_LONG_PLACEHOLDER);
    inputFocusSubject.next(true);

    if (toggleWidth) {
      togglePlaceholderWidth(true);
    }
  };

  const closeHandler = () => {
    setIsActive(false);
    setToggleWidthPlaceholder(SEARCH_SHORT_PLACEHOLDER);
    closeAutocompleteSubject.next(true);

    if (toggleWidth) {
      togglePlaceholderWidth(false);
    }
    if (isMobileNavSearchElement) {
      document.dispatchEvent(closeMobileSearchBlock);
    }
  };

  useEffect(() => {
    initSearchElement();
    removeWebflowSearchElements();
  }, []);

  useEffect(() => {
    if (isActive) {
      openHandler();
    } else {
      closeHandler();
    }
  }, [isActive]);

  return (
    <SearchAutoSuggest
      onFocus={openHandler}
      onKeyDown={keyDownHandler}
      startIcon={<StyleSearchAutoSuggestIcon onClick={onSearchIconClick} />}
      placeholder={toggleWidth ? toggleWidthPlaceholder : SEARCH_LONG_PLACEHOLDER}
      isActive={isActive}
      inputFocusSubject={inputFocusSubject}
      closeAutocompleteSubject={closeAutocompleteSubject}
      onInputChangeCallBack={setInput}
      endIcon={
        (showCloseButton && input) || isMobileNavSearchElement ? (
          <HighlightOffIcon onClick={closeHandler} style={{ color: palette.custom.grey }} />
        ) : undefined
      }
    />
  );
}

export default EquipmentSearchAutoSuggest;
